<template>
    <div class="max-w-sm px-20">
        <h2 class="auth-title mb-10">{{ $t('auth.register.title') }}</h2>
        <p v-if="showForm && isAnonymous" class="text-center">{{ $t('auth.register.text') }}</p>
        <p v-if="showForm && !isAnonymous" class="text-center">{{ $t('auth.register.complete') }}</p>

        <social-login @loggedIn="startLogin"/>

        <FormulateForm
            v-if="showForm"
            class="mt-5"
            v-model="formValues"
            @submit="register">

            <FormulateInput class="formulateinput"
                ref="email"
                type="email"
                name="email"
                :placeholder="$t('auth.register.email')"
                validation="required|email|emailAlreadyTaken"
                :validation-rules="{emailAlreadyTaken: emailAlreadyTaken}"
                :validation-messages="validationMessages"
                input-class="skitude-input"
                input-has-errors-class="skitude-input-error"
                element-class="skitude-element"
                error-has-errors-class="skitude-validation"
            />

            <FormulateInput class="formulateinput"
                ref="name"
                type="text"
                name="name"
                :placeholder="$t('auth.register.name')"
                validation="required"
                input-class="skitude-input"
                input-has-errors-class="skitude-input-error"
                element-class="skitude-element"
                error-has-errors-class="skitude-validation"
            />

            <FormulateInput class="formulateinput"
                ref="surname"
                type="text"
                name="surname"
                :placeholder="$t('auth.register.surname')"
                validation="required"
                input-class="skitude-input"
                input-has-errors-class="skitude-input-error"
                element-class="skitude-element"
                error-has-errors-class="skitude-validation"
            />

            <FormulateInput class="formulateinput"
                ref="username"
                type="text"
                name="username"
                :placeholder="$t('auth.register.username')"
                validation="required|usernameAlreadyTaken"
                input-class="skitude-input"
                input-has-errors-class="skitude-input-error"
                element-class="skitude-element"
                error-has-errors-class="skitude-validation"
                :validation-rules="{usernameAlreadyTaken: usernameAlreadyTaken}"
                :validation-messages="validationMessages"
            />

            <FormulateInput class="formulateinput"
                ref="birthdate"
                type="datewithplaceholder"
                name="birthdate"
                :placeholder="$t('auth.register.birthdate')"
                validation="required"
                input-class="skitude-input"
                input-has-errors-class="skitude-input-error"
                element-class="skitude-element"
                error-has-errors-class="skitude-validation"
            />

            <FormulateInput class="formulateinput"
                ref="password"
                type="password"
                name="password"
                :placeholder="$t('auth.register.password')"
                validation="required"
                input-class="skitude-input"
                input-has-errors-class="skitude-input-error"
                element-class="skitude-element"
                error-has-errors-class="skitude-validation"
            />

            <ria-checkbox 
                :label="$t('auth.register.newsletter_checkbox')" 
                fontSize="xs"
                :checked="false"
                :required="false"
                ref="acceptCommunications" />

            <ria-checkbox :label="$t('auth.register.privacy_policy')" 
                fontSize="xs"
                :checked="false"
                :required="true"
                class="mt-3"
                ref="acceptTOS" 
                @changed="policyChanged"
                />
            <ria-button type="submit" class="block mx-auto mt-4 text-base" :fullwidth="true" :text="$t('auth.register.register')" :action="dummy" :active="canRegister"/>
        
        </FormulateForm>

         <div v-if="!showForm">
            <p>{{ $t('auth.register.register_complete') }}</p>
            <ria-button type="button" class="block mx-auto mt-4 text-base" :fullwidth="true" text="Close" :action="startLogin"/>
        </div>

    </div>
</template>

<script>
    import RiaButton from "@/components/common/ria-button"
    // import RiaInput from "@/components/form/elements/ria-input";
    import RiaCheckbox from "@/components/form/elements/ria-checkbox";
    import RegisterForm from "@/services/auth/registerForm"
    import SocialLogin from "@/components/auth/social-login";
    import { apiClient } from "@/api/index"
    import { mapGetters, mapActions } from 'vuex';
    import TokenManager from "@/api/tokenManager/TokenManager";
    import i18n from '@/i18n';

    export default {
        name: "register",
        components : {
            RiaButton,
            // RiaInput,
            RiaCheckbox,
            SocialLogin,
        },
        data: function() {
            return {
                formValues: {},
                showForm: true,
                hasErrors: false,
                validationCustomRules: Object,
                validationMessages: { 
                    usernameAlreadyTaken: i18n.t('auth.register.username_taken'), 
                    emailAlreadyTaken: i18n.t('auth.register.email_taken'),
                },
                canRegister: false
            }
        },
        computed: {
            ...mapGetters("auth", ["isAnonymous"]),
        },
        methods: {
            ...mapActions("auth", ["setLogged"]),
            dummy() {},
            createForm() {
                return RegisterForm.createFromForm(
                    this.formValues.email,
                    this.formValues.name,
                    this.formValues.surname,
                    this.formValues.username,
                    this.formValues.birthdate,
                    this.formValues.password,
                    this.$refs.acceptCommunications.enabled(),
                    this.$refs.acceptTOS.enabled(),
                )
            },
            policyChanged({status}) {
                this.canRegister = status;
            },
            async register() {
                if (!this.$refs.acceptTOS.enabled()) {
                    return;
                }

                const form = this.createForm()

                apiClient.skitudeRegister(form).then(() => {
                    if (!this.isAnonymous) {
                        this.setLogged()
                    }
                    this.showForm = false
                }).catch((error) => {
                    console.error(error);
                    const event = new CustomEvent('errorModal', {
                        detail: {
                            body: i18n.t('auth.login.error')
                        } 
                    });
                    window.dispatchEvent(event)
                });
            },
            startLogin() {
                this.$emit('startLogin')
            },
            async emailAlreadyTaken ({value}) {
                if (value === '') {
                    return Promise.resolve(false)
                }
                return apiClient.skitudeRegisterCheckEmail(value).then(
                    (hasEmail) => Promise.resolve(!hasEmail.data.object.exists)
                )
            },
            async usernameAlreadyTaken ({value}) {
                if (value === '') {
                    return Promise.resolve(false)
                }
                return apiClient.skitudeRegisterCheckUsername(value).then(
                    (hasUsername) => Promise.resolve(!hasUsername.data.object.exists)
                )
            },
        },
        mounted() {
            if (!this.isAnonymous) {
                const tokenManager = new TokenManager()
                this.$refs.email.value = tokenManager.getFirebaseSDK().auth().currentUser.email
            }
        }   
    };
</script>

<style scoped>

</style>

